import { ThemeColors } from '~/themes/colors'

interface QuickActionColors {
  Background: ThemeColors
  Color: ThemeColors
  Hover: {
    Background: ThemeColors
    Color: ThemeColors
  }
  Active: {
    Background: ThemeColors
    Color: ThemeColors
  }
  Disabled: {
    Background: ThemeColors
    Color: ThemeColors
  }
}

interface QuickActionWithShadowColors extends QuickActionColors {
  Hover: QuickActionColors['Hover'] & { BoxShadow: ThemeColors }
  Active: QuickActionColors['Active'] & { BoxShadow: ThemeColors }
}

interface QuickActionButtonColors {
  Default_400: {
    Background: {
      GradientColor1: ThemeColors
      GradientColor2: ThemeColors
      GradientColor3: ThemeColors
    }
    Color: ThemeColors
    Hover: {
      Background: {
        GradientColor1: ThemeColors
        GradientColor2: ThemeColors
        GradientColor3: ThemeColors
      }
      Color: ThemeColors
    }
    Active: {
      Background: {
        GradientColor1: ThemeColors
        GradientColor2: ThemeColors
      }
      Color: ThemeColors
    }
    Disabled: {
      Background: ThemeColors
      Color: ThemeColors
    }
  }
  Default_300: QuickActionWithShadowColors
  Default_200: QuickActionColors
  Default_150: QuickActionColors
  Default_100: QuickActionColors
  Destructive_300: QuickActionWithShadowColors
  Destructive_200: QuickActionColors
  Destructive_100: QuickActionColors
  Active_200: QuickActionColors
}

export const quickActionButtonColors: QuickActionButtonColors = {
  Default_400: {
    Background: {
      GradientColor1: {
        default: 'mango.purple',
      },
      GradientColor2: {
        default: 'blue.900',
      },
      GradientColor3: {
        default: 'mango.aqua',
      },
    },
    Color: {
      default: 'white',
    },
    Hover: {
      Background: {
        GradientColor1: {
          default: 'mango.aqua',
        },
        GradientColor2: {
          default: 'blue.900',
        },
        GradientColor3: {
          default: 'mango.purple',
        },
      },
      Color: {
        default: 'white',
      },
    },
    Active: {
      Background: {
        GradientColor1: {
          default: 'mango.aqua',
        },
        GradientColor2: {
          default: 'blue.900',
        },
      },
      Color: {
        default: 'white',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.700.40',
      },
      Color: {
        default: 'white',
      },
    },
  },
  Default_300: {
    Background: {
      default: 'blue.900',
    },
    Color: {
      default: 'white',
    },
    Hover: {
      Background: {
        default: 'blue.800',
      },
      BoxShadow: {
        default: 'mask.blue.700.20',
      },
      Color: {
        default: 'white',
      },
    },
    Active: {
      Background: {
        default: 'blue.1100',
      },
      BoxShadow: {
        default: 'mask.blue.700.20',
      },
      Color: {
        default: 'white',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.700.40',
      },
      Color: {
        default: 'white',
      },
    },
  },
  Default_200: {
    Background: {
      default: 'mask.gray.700.20',
    },
    Color: {
      default: 'gray.1300',
    },
    Hover: {
      Background: {
        default: 'mask.blue.300.60',
      },
      Color: {
        default: 'blue.900',
      },
    },
    Active: {
      Background: {
        default: 'blue.300',
      },
      Color: {
        default: 'blue.900',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.600.20',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
  Default_150: {
    Background: {
      default: 'achromatic.transparent',
    },
    Color: {
      default: 'gray.1300',
    },
    Hover: {
      Background: {
        default: 'mask.blue.300.60',
      },
      Color: {
        default: 'blue.900',
      },
    },
    Active: {
      Background: {
        default: 'blue.300',
      },
      Color: {
        default: 'blue.900',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.600.20',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
  Default_100: {
    Background: {
      default: 'achromatic.transparent',
    },
    Color: {
      default: 'gray.1300',
    },
    Hover: {
      Background: {
        default: 'mask.gray.700.20',
      },
      Color: {
        default: 'gray.1300',
      },
    },
    Active: {
      Background: {
        default: 'mask.gray.700.40',
      },
      Color: {
        default: 'gray.1300',
      },
    },
    Disabled: {
      Background: {
        default: 'achromatic.transparent',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
  Destructive_300: {
    Background: {
      default: 'red.800',
    },
    Color: {
      default: 'white',
    },
    Hover: {
      Background: {
        default: 'red.700',
      },
      BoxShadow: {
        default: 'mask.red.600.20',
      },
      Color: {
        default: 'white',
      },
    },
    Active: {
      Background: {
        default: 'red.900',
      },
      BoxShadow: {
        default: 'mask.red.600.20',
      },
      Color: {
        default: 'white',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.700.40',
      },
      Color: {
        default: 'white',
      },
    },
  },
  Destructive_200: {
    Background: {
      default: 'red.200',
    },
    Color: {
      default: 'red.800',
    },
    Hover: {
      Background: {
        default: 'red.300',
      },
      Color: {
        default: 'red.800',
      },
    },
    Active: {
      Background: {
        default: 'red.400',
      },
      Color: {
        default: 'red.800',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.600.20',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
  Destructive_100: {
    Background: {
      default: 'achromatic.transparent',
    },
    Color: {
      default: 'red.800',
    },
    Hover: {
      Background: {
        default: 'red.200',
      },
      Color: {
        default: 'red.800',
      },
    },
    Active: {
      Background: {
        default: 'red.300',
      },
      Color: {
        default: 'red.800',
      },
    },
    Disabled: {
      Background: {
        default: 'achromatic.transparent',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
  Active_200: {
    Background: {
      default: 'gray.1300',
    },
    Color: {
      default: 'white',
    },
    Hover: {
      Background: {
        default: 'blue.900',
      },
      Color: {
        default: 'white',
      },
    },
    Active: {
      Background: {
        default: 'blue.1100',
      },
      Color: {
        default: 'white',
      },
    },
    Disabled: {
      Background: {
        default: 'mask.gray.600.20',
      },
      Color: {
        default: 'gray.600',
      },
    },
  },
}
